import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { PropsWithChildren } from 'react'

import { authApi } from '@/lib/api/auth'
import { tokenStorage } from '@/lib/storage/tokenStorage'
import type { AuthTokens, AuthUser, LoginCredentials, UserRole } from '@/types/auth'

interface AuthContextValue {
  user: AuthUser | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<AuthTokens | null>
  hasRole: (roles: UserRole | UserRole[]) => boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const mapTokens = (response: { token?: string; accessToken?: string; refreshToken?: string; expiresIn?: number; refreshExpiresIn?: number }): AuthTokens => {
  // Handle JWT-only response from kiroSupport backend
  if (response.token) {
    // Backend JWT expires in 7 days for admin/agent users.
    // Set frontend expiry to match (minus 5min buffer for proactive refresh).
    // No separate refresh token exists — use the same JWT.
    return {
      accessToken: response.token,
      refreshToken: response.token, // Use same token since backend doesn't provide refresh tokens
      expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days (matches backend JWT expiry)
      refreshExpiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days
    }
  }

  // Handle legacy access/refresh token format
  return {
    accessToken: response.accessToken!,
    refreshToken: response.refreshToken!,
    expiresAt: Date.now() + (response.expiresIn || 3600) * 1000,
    refreshExpiresAt: response.refreshExpiresIn
      ? Date.now() + response.refreshExpiresIn * 1000
      : undefined,
  }
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(tokenStorage.get())
  const [isLoading, setIsLoading] = useState(true)

  const syncTokens = useCallback((incomingTokens: AuthTokens | null) => {
    if (incomingTokens) {
      tokenStorage.set(incomingTokens)
      setTokens(incomingTokens)
    } else {
      tokenStorage.clear()
      setTokens(null)
    }
  }, [])

  const hydrateProfile = useCallback(async () => {
    const storedTokens = tokenStorage.get()

    if (!storedTokens) {
      setIsLoading(false)
      return
    }

    syncTokens(storedTokens)

    try {
      const profile = await authApi.me()
      setUser(profile)
    } catch {
      syncTokens(null)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [syncTokens])

  useEffect(() => {
    void hydrateProfile()
  }, [hydrateProfile])

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setIsLoading(true)
      try {
        const response = await authApi.login(credentials)
        const preparedTokens = mapTokens(response)

        // Transform user object to match expected format
        const transformedUser: AuthUser = {
          id: response.user.id,
          email: response.user.email,
          name: response.user.name,
          roles: [response.user.role],
          permissions: [],
        }

        syncTokens(preparedTokens)
        setUser(transformedUser)
      } finally {
        setIsLoading(false)
      }
    },
    [syncTokens],
  )

  const logout = useCallback(async () => {
    setIsLoading(true)
    try {
      await authApi.logout()
    } finally {
      syncTokens(null)
      setUser(null)
      setIsLoading(false)
    }
  }, [syncTokens])

  const refresh = useCallback(async () => {
    const refreshedTokens = await authApi.refresh()

    if (!refreshedTokens) {
      syncTokens(null)
      setUser(null)
      return null
    }

    syncTokens(refreshedTokens)
    return refreshedTokens
  }, [syncTokens])

  const hasRole = useCallback(
    (roles: UserRole | UserRole[]) => {
      if (!user) {
        return false
      }

      const roleList = Array.isArray(roles) ? roles : [roles]
      return roleList.some((role) => user.roles.includes(role))
    },
    [user],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      tokens,
      isAuthenticated: Boolean(user && tokens),
      isLoading,
      login,
      logout,
      refresh,
      hasRole,
    }),
    [user, tokens, isLoading, login, logout, refresh, hasRole],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return context
}
